import axios from 'axios'
import {Raven} from 'vue-raven'
import {
    DOC_METADATA_GENERATE_FROM_SETTINGS,
    DOC_METADATA_RESET,
    DOC_METADATA_SET,
    DOC_METADATA_VALIDATE_FORM
} from './metadata'
import Vue from 'vue'
import _, {get, isEqual} from 'lodash'
import {FormExtensions, getFormExtensionByName} from '@/types/document-viewer/extension'
import {
    MATCHING_EXTENSION_DEFAULT_TOTAL_DEVIATION_PERCENT,
    MatchingExtensionProcessLevel
} from '@/types/document-viewer/extension/matching'
import {DOC_PO_CHECK} from './matching/purchase-order'
import {DOC_RECEIPT_CHECK} from './matching/supplier-receipt'
import {FieldEditorNumericSeparators} from '@/types/document-viewer/field/modules/editor/numeric'
import {DocTaxCalculationFormulas} from '@/types/document-viewer/extension/calculate/totals/tax'
import {DOC_ADD_HIDDEN_FIELDS_INTO_MODEL, DOC_ADJUST_LINES_TYPE} from './index'
import {FormSystemSections, getDefaultSystemSectionByKey} from '@/types/document-viewer/section'
import {FieldEditorTypes} from '@/types/document-viewer/field/modules/editor'
import {FieldEditorOptionsSources} from '@/types/document-viewer/field/modules/editor/select'
import {LineTotalCalculationFormulas} from '@/types/document-viewer/extension/calculate/lines/total'
import {LineTaxCalculationFormulas} from '@/types/document-viewer/extension/calculate/lines/tax'
import {checkIsVisibleFieldInAccordanceWithRules} from '@/types/document-viewer/field/modules/rules'
import {showErrorMessage} from '@/services/snackbar'
import {getFieldPathMeta} from '@/types/document-viewer/field/modules/path'
import {AppDocSystemTypes} from '@/types/document/types'
import {QuickActionAvailableIf} from '@/views/documents/subtypes/settings/quick-actions'

export const DOC_SETTINGS_SETUP_WITH_METADATA = 'DOC_SETTINGS_SETUP_WITH_METADATA'
export const DOC_SETTINGS_RESET = 'DOC_SETTINGS_RESET'
export const DOC_SETTINGS_SET = 'DOC_SETTINGS_SET'
export const DOC_SETTINGS_SET_ROOT_PROPS = 'DOC_SETTINGS_SET_ROOT_PROPS'
export const DOC_SETTINGS_UPDATE = 'DOC_SETTINGS_UPDATE'
export const DOC_SETTINGS_FETCH = 'DOC_SETTINGS_FETCH'
export const DOC_SETTINGS_SET_IS_EDIT_MODE = 'DOC_SETTINGS_SET_IS_EDIT_MODE'
export const DOC_SETTINGS_SET_IS_FETCHING = 'DOC_SETTINGS_SET_IS_FETCHING'
export const DOC_SETTINGS_SET_SHOW_EXTENSION_HINTS = 'DOC_SETTINGS_SET_SHOW_EXTENSION_HINTS'

export const DOC_SETTINGS_SET_VISIBLE_FIELDS_WITH_STATE_RULES_APPLIED = 'DOC_SETTINGS_SET_VISIBLE_FIELDS_WITH_STATE_RULES_APPLIED'
export const DOC_SETTINGS_REFRESH_VISIBLE_FIELDS_WITH_STATE_RULES_APPLIED = 'DOC_SETTINGS_REFRESH_VISIBLE_FIELDS_WITH_STATE_RULES_APPLIED'

// *This store is used by document subtype settings modal and doc viewer form as there are the same

const initialState = () => ({
    /*
    * NOTE: there are some historical conflicts in names:
    * 'type' == 'section' field in document data and subtype settings
    * 'subtype' == 'type' field in document data but 'key' in subtype settings
    * */

    rootSettings: {
        display: '', // Invoice
        property: '', // "INVOICE"
        section: '', // "PURCHASING"
        global: true,
        isDefault: false,
        isDefaultPurchasing: false,
        isDefaultSales: 0,
        isHidden: 0,
        isTemplate: 0,
        order: 0
    },
    settings: { // form settings
        sections: [],
        fields: [],
        extensions: [],
        quickActions: [],
        toolbar: {}
    },

    visibleFieldsWithStateRulesApplied: [],

    isSettingsEditMode: false,
    isSettingsFetching: false,

    showExtensionHints: false
})

export const state = initialState()

export const getters = {
    rootSettings: state => state.rootSettings || {},
    // see FormSettings interface
    settings: state => state.settings || {},
    toolbarSettings: (state, getters) => getters.settings.toolbar || {},
    extensions: (state, getters) => getters.settings.extensions || [],
    quickActions: (state, getters) => getters.settings.quickActions || [],
    availableQuickActions: (state, getters, rootState, rootGetters) => getters.quickActions.filter(qa => {
        return qa.availableIf === QuickActionAvailableIf.ALWAYS ||
            (!qa.availableIf && !rootGetters['doc_viewer/disabledDocument']) ||
            (qa.availableIf === QuickActionAvailableIf.COMPLETE && rootGetters['doc_viewer/isCompletedDoc']) ||
            (qa.availableIf === QuickActionAvailableIf.NOT_COMPLETE && !rootGetters['doc_viewer/isCompletedDoc'])
    }),
    quickActionsWithHotKey: (state, getters) => getters.availableQuickActions.filter(qa => Array.isArray(qa.hotkey) && qa.hotkey.length > 0),
    sections: (state, getters) => getters.settings.sections || [],

    fields: (state, getters) => getters.settings.fields || [],
    /*
    * 'visibleFields' - visible in accordance with form settings only
    * */
    visibleFields: (state, getters) => getters.fields.filter(f => f.visible) || [],
    /*
     * 'visibleFieldsWithStateRulesApplied' - visible in accordance with form settings and current document state.
     * (excludes fields that don't match the visibility rules based on the current state of the document)
     * Note: form metadata is not regenerated on this list update.
     * */
    visibleFieldsWithStateRulesApplied: state => state.visibleFieldsWithStateRulesApplied || [],

    showExtensionHints: state => state.showExtensionHints,
    isSettingsEditMode: state => state.isSettingsEditMode,
    isSettingsFetching: state => state.isSettingsFetching,

    getFieldSettingsByPath: (state, getters) => path => getters.fields.find(f => f.path === path),
    checkIsVisibleFieldByPath: (state, getters) => path => !!getters.getFieldSettingsByPath(path) && !!getters.getFieldSettingsByPath(path).visible,
    checkIsVisibleFieldWithStateRulesApplied: (state, getters) => path => !!getters.visibleFieldsWithStateRulesApplied.find(f => f.path === path),
    checkIsAvailableExtension: (state, getters) => name => {
        const ext = getFormExtensionByName(name)
        return ext && ext.available && ext.available.validator &&
            typeof ext.available.validator === 'function' &&
            ext.available.validator(getters.settings)
    },

    recurDocumentExtension: (state, getters) => getters.extensions.find(e => e.name === FormExtensions.RECUR_DOCUMENT),
    calculateDocSubtotal: (state, getters) => getters.extensions.some(e => e.name === FormExtensions.CALCULATE_DOCUMENT_SUBTOTAL),
    calculateDocFreight: (state, getters) => getters.extensions.some(e => e.name === FormExtensions.CALCULATE_DOCUMENT_FREIGHT),

    calculateDocTotal: (state, getters) => !!getters.calculateDocTotalExtension,
    calculateDocTotalExtension: (state, getters) => getters.extensions.find(e => e.name === FormExtensions.CALCULATE_DOCUMENT_TOTAL),
    calculateDocTotalFormulaKey: (state, getters) => _.get(getters.calculateDocTotalExtension, ['params', 'formulaKey']),

    calculateDocTax: (state, getters) => !!getters.calculateDocTaxExtension,
    calculateDocTaxAndOverride: (state, getters) => getters.calculateDocTax &&
        (!getters.calculateDocTaxExtension.params || !getters.calculateDocTaxExtension.params.viewOnly),
    calculateDocTaxExtension: (state, getters) => getters.extensions.find(e => e.name === FormExtensions.CALCULATE_DOCUMENT_TAX),
    calculateDocTaxFormulaKey: (state, getters) =>
        _.get(getters.calculateDocTaxExtension, ['params', 'formulaKey'], DocTaxCalculationFormulas.TAX_EQUAL_LINES_TAX_SUM),

    calculateLineTaxExtension: (state, getters) => getters.extensions.find(e => e.name === FormExtensions.CALCULATE_LINE_TAX),
    calculateLineTax: (state, getters) => !!getters.calculateLineTaxExtension,
    calculateLineTaxFormulaKey: (state, getters) => {
        if (!getters.calculateLineTaxExtension) {
            return null
        }
        return _.get(getters.calculateLineTaxExtension, ['params', 'formulaKey'], LineTaxCalculationFormulas.TAX_FROM_TOTAL_BY_TAX_RATE)
    },

    calculateLineTotalExtension: (state, getters) => getters.extensions.find(e => e.name === FormExtensions.CALCULATE_LINE_TOTAL),
    calculateLineTotal: (state, getters) => !!getters.calculateLineTotalExtension,
    calculateLineTotalFormulaKey: (state, getters) => {
        if (!getters.calculateLineTotalExtension) {
            return null
        }
        return _.get(getters.calculateLineTotalExtension, ['params', 'formulaKey'], LineTotalCalculationFormulas.PURCHASE_TOTAL_GENERAL)
    },
    autocompleteStatementLineExtension: (state, getters) => getters.extensions.find(e => e.name === FormExtensions.AUTOCOMPLETE_STATEMENT_LINE),
    calculateStatementLineTotals: (state, getters) => !!getters.autocompleteStatementLineExtension,

    validateNoNewLineItems: (state, getters) => getters.extensions.some(e => e.name === FormExtensions.VALIDATE_NO_NEW_LINE_ITEM),
    validateNoNewContact: (state, getters) => getters.extensions.some(e => e.name === FormExtensions.VALIDATE_NO_NEW_CONTACT),
    validateDocumentDueDate: (state, getters) => getters.extensions.some(e => e.name === FormExtensions.VALIDATE_DOCUMENT_DUE_DATE),
    validateExpenseSplit: (state, getters) => getters.extensions.some(e => e.name === FormExtensions.VALIDATE_EXPENSE_SPLIT),
    validateDocumentTax: (state, getters) => getters.extensions.some(e => e.name === FormExtensions.VALIDATE_DOCUMENT_TAX),
    validatePurchaseTotal: (state, getters) => getters.extensions.some(e => e.name === FormExtensions.VALIDATE_PURCHASE_TOTAL),
    validateLinesTotal: (state, getters) => getters.extensions.some(e => e.name === FormExtensions.VALIDATE_LINES_TOTAL),
    validateDocumentNotes: (state, getters) => getters.extensions.some(e => e.name === FormExtensions.VALIDATE_DOCUMENT_NOTES),
    checkContactBankDetails: (state, getters) => getters.extensions.some(e => e.name === FormExtensions.CONTACT_BANK_DETAILS),
    checkContactABN: (state, getters) => getters.extensions.some(e => e.name === FormExtensions.CONTACT_ABN),

    assigneesAddon: (state, getters) => getters.extensions.find(e => e.name === FormExtensions.DOC_ASSIGNEES),
    minAssigneesRequired: (state, getters) => getters.assigneesAddon ? get(getters.assigneesAddon, 'params.minAssigneesRequired', 0) : 0,

    locationsAddon: (state, getters) => getters.extensions.find(e => e.name === FormExtensions.DOC_LOCATIONS),
    locationRequiredForEachLine: (state, getters) => getters.locationsAddon ? get(getters.locationsAddon, 'params.locationRequiredForEachLine', false) : false,

    subsidiariesAddon: (state, getters) => getters.extensions.find(e => e.name === FormExtensions.DOC_SUBSIDIARIES),
    subsidiaryRequiredForEachLine: (state, getters) => getters.subsidiariesAddon ? get(getters.subsidiariesAddon, 'params.subsidiaryRequiredForEachLine', false) : false,

    hideLineTableColumnsWithoutOptions: (state, getters) => !getters.isSettingsEditMode &&
        getters.extensions.some(e => e.name === FormExtensions.HIDE_LINE_COLUMNS_WITH_EMPTY_SELECT),
    allowLineCreation: (state, getters) => getters.extensions.some(e => e.name === FormExtensions.LINE_CREATION),

    contactSelectExtension: (state, getters) => getters.extensions.find(e => e.name === FormExtensions.CONTACT_SELECT),
    confirmContactCreation: (state, getters) => getters.extensions.some(e => e.name === FormExtensions.CONFIRM_CONTACT_CREATION),
    confirmItemCreation: (state, getters) => getters.extensions.some(e => e.name === FormExtensions.CONFIRM_ITEM_CREATION) &&
        getters.getFieldSettingsByPath('lines.sku') && getters.getFieldSettingsByPath('lines.sku').visible,

    withLineUomConversion: (state, getters) => getters.extensions.some(e => e.name === FormExtensions.LINE_CONVERSION) &&
        getters.getFieldSettingsByPath('lines.sku') && getters.getFieldSettingsByPath('lines.sku').visible,

    matchItemExtension: (state, getters) => getters.extensions.find(e => e.name === FormExtensions.MATCH_ITEM),
    noItemAlternativeAlert: (state, getters) => !!getters.extensions.find(e => e.name === FormExtensions.NO_ITEM_ALTERNATIVE_ALERT),

    poExtension: (state, getters) => getters.extensions.find(e => e.name === FormExtensions.PURCHASE_ORDER), /* FormExtension */
    poCheckEnabled: (state, getters) => !!getters.poExtension,
    poExtensionParams: (state, getters) => _.get(getters.poExtension, 'params'),
    poAutofill: (state, getters) => _.get(getters.poExtensionParams, 'autofill') /* MatchingExtensionAutofill */,
    poAutofillForce: (state, getters) => {
        return getters.poExtensionParams && getters.poExtensionParams.hasOwnProperty('forceOverride')
            ? getters.poExtensionParams.forceOverride
            : true
    },
    poProcessLevel: (state, getters) => _.get(getters.poExtensionParams, 'level', MatchingExtensionProcessLevel.LINES),
    poMatchLinesBy: (state, getters) => _.get(getters.poExtensionParams, 'matchLinesBy'),
    poRequiredToProcess: (state, getters) => Boolean(_.get(getters.poExtensionParams, 'required')),
    poAllLinesShouldBeMatched: (state, getters) => Boolean(_.get(getters.poExtensionParams, 'allLinesShouldBeMatched')),
    poTotalDeviationCheckRequiredToProcess: (state, getters) => Boolean(_.get(getters.poExtensionParams, 'totalDeviationCheckRequiredToProcess')),
    poTotalDeviationPercentAllowed: (state, getters) => _.isNumber(_.get(getters.poExtensionParams, 'totalDeviationPercentAllowed'))
        ? getters.poExtensionParams.totalDeviationPercentAllowed
        : MATCHING_EXTENSION_DEFAULT_TOTAL_DEVIATION_PERCENT,

    supplierReceiptsExtension: (state, getters) => getters.extensions.find(e => e.name === FormExtensions.SUPPLIER_RECEIPT), /* FormExtension */
    supplierReceiptsCheckEnabled: (state, getters) => !!getters.supplierReceiptsExtension,
    srExtensionParams: (state, getters) => _.get(getters.supplierReceiptsExtension, 'params'),
    supplierReceiptsAutofill: (state, getters) => _.get(getters.srExtensionParams, 'autofill') /* MatchingExtensionAutofill */,
    supplierReceiptsAutofillForce: (state, getters) => {
        return getters.srExtensionParams && getters.srExtensionParams.hasOwnProperty('forceOverride')
            ? getters.srExtensionParams.forceOverride
            : true
    },
    supplierReceiptsProcessLevel: (state, getters) => _.get(getters.srExtensionParams, 'level', MatchingExtensionProcessLevel.LINES),
    supplierReceiptsMatchLinesBy: (state, getters) => _.get(getters.srExtensionParams, 'matchLinesBy'),
    supplierReceiptsRequiredToProcess: (state, getters) => Boolean(_.get(getters.srExtensionParams, 'required')),
    supplierReceiptsAllLinesShouldBeMatched: (state, getters) => Boolean(_.get(getters.srExtensionParams, 'allLinesShouldBeMatched')),
    supplierReceiptsTotalCheckRequiredToProcess: (state, getters) => Boolean(_.get(getters.srExtensionParams, 'totalDeviationCheckRequiredToProcess')),
    supplierReceiptsTotalDeviationPercentAllowed: (state, getters) => _.isNumber(_.get(getters.srExtensionParams, 'totalDeviationPercentAllowed'))
        ? getters.srExtensionParams.totalDeviationPercentAllowed
        : MATCHING_EXTENSION_DEFAULT_TOTAL_DEVIATION_PERCENT,

    blockProcessingWithItemPriceAboveBlockingLevel: (state, getters) => {
        return getters.extensions.some(e => e.name === FormExtensions.BLOCK_PROCESSING_IF_ITEM_PRICE_ABOVE_BLOCKING_LEVEL)
    },
    closeModalOnStatusChangeTo: (state, getters) => {
        if (getters.extensions.some(e => e.name === FormExtensions.CLOSE_MODAL &&
            e.params && _.isArray(e.params.statusChangedTo) && e.params.statusChangedTo.length !== 0)) {
            return getters.extensions.find(e => e.name === FormExtensions.CLOSE_MODAL).params.statusChangedTo
        }
        return []
    },

    hasLinesSplitAndTotal: (state, getters) => {
        return getters.fields.some(f => f.path === 'lines.total' && !!f.visible) &&
            getters.fields.some(f => f.path === 'lines.split' && !!f.visible)
    },

    defaultRounding: () => 4,
    getRoundingDecimalsByFieldPath: (state, getters, rootState, rootGetters) => path => {
        const settings = getters.getFieldSettingsByPath(path)
        if (settings && settings.editor && settings.editor.numeric && _.isNumber(settings.editor.numeric.precision)) {
            return settings.editor.numeric.precision
        }
        return rootGetters['doc_viewer_settings/defaultRounding']
    },

    getThousandSeparatorByFieldPath: (state, getters) => path => {
        const settings = getters.getFieldSettingsByPath(path)
        if (settings && settings.editor && settings.editor.numeric &&
            Object.values(FieldEditorNumericSeparators).includes(settings.editor.numeric.thousandSeparator)) {
            return settings.editor.numeric.thousandSeparator
        }
        return ''
    }
}

export const mutations = {
    [DOC_SETTINGS_RESET]: (state) => {
        const s = initialState()
        Object.keys(s).forEach(key => {
            state[key] = s[key]
        })
    },
    [DOC_SETTINGS_SET]: (state, data) => {
        state.settings = data
    },
    [DOC_SETTINGS_SET_ROOT_PROPS]: (state, data) => {
        state.rootSettings = data
    },
    [DOC_SETTINGS_UPDATE]: (state, {prop, data}) => {
        Vue.set(state.settings, prop, data)
    },
    [DOC_SETTINGS_SET_IS_FETCHING]: (state, value) => {
        state.isSettingsFetching = value
    },
    [DOC_SETTINGS_SET_IS_EDIT_MODE]: (state, value) => {
        state.isSettingsEditMode = value
    },
    [DOC_SETTINGS_SET_SHOW_EXTENSION_HINTS]: (state, value) => {
        state.showExtensionHints = value
    },
    [DOC_SETTINGS_SET_VISIBLE_FIELDS_WITH_STATE_RULES_APPLIED]: (state, fields) => {
        state.visibleFieldsWithStateRulesApplied = fields
    }
}

export const actions = {
    [DOC_SETTINGS_SETUP_WITH_METADATA]: async({commit, dispatch, rootGetters}) => {
        try {
            commit(DOC_SETTINGS_SET_IS_FETCHING, true)
            await dispatch(DOC_SETTINGS_FETCH, {})
            await dispatch(`doc_viewer_metadata/${DOC_METADATA_RESET}`, null, {root: true})
            const formMetadata = await dispatch(`doc_viewer_metadata/${DOC_METADATA_GENERATE_FROM_SETTINGS}`, null, {root: true})
            commit(`doc_viewer_metadata/${DOC_METADATA_SET}`, formMetadata, {root: true})
            dispatch(`doc_viewer_metadata/${DOC_METADATA_VALIDATE_FORM}`, null, {root: true})
            if (!rootGetters['doc_viewer_po/purchaseOrder'] || !rootGetters['doc_viewer_po/purchaseOrder'].uuid) {
                dispatch(`doc_viewer_po/${DOC_PO_CHECK}`, null, {root: true})
            }
            if (!rootGetters['doc_viewer_supplier_receipt/deliveryDocket'] || !rootGetters['doc_viewer_supplier_receipt/deliveryDocket'].uuid) {
                dispatch(`doc_viewer_supplier_receipt/${DOC_RECEIPT_CHECK}`, null, {root: true})
            }
            dispatch(`doc_viewer/${DOC_ADJUST_LINES_TYPE}`, null, {root: true})
            dispatch(`doc_viewer/${DOC_ADD_HIDDEN_FIELDS_INTO_MODEL}`, null, {root: true})
            await dispatch(DOC_SETTINGS_REFRESH_VISIBLE_FIELDS_WITH_STATE_RULES_APPLIED)
        } catch (e) {
            Raven.captureException(e)
        } finally {
            commit(DOC_SETTINGS_SET_IS_FETCHING, false)
        }
    },
    [DOC_SETTINGS_FETCH]: async({commit, rootGetters}, {type, subtype}) => {
        try {
            if (rootGetters['documents/isDemoContracts']) {
                type = AppDocSystemTypes.DEMO_CONTRACTS
            } else if (!type) {
                type = rootGetters['doc_viewer/doc'].section
            }

            if (!subtype) {
                subtype = rootGetters['doc_viewer/doc'].type
            }
            const res = await axios.get(`documents/types/${type}/subtypes/${subtype}`, {requestId: DOC_SETTINGS_FETCH})
            const allSettings = _.get(res, 'data.data', {})
            const rootSettings = _.omit(allSettings, 'settings')
            const settings = getValidatedFormSettings({settings: allSettings.settings, type})
            commit(DOC_SETTINGS_SET_ROOT_PROPS, {...rootSettings, section: type, property: subtype})
            commit(DOC_SETTINGS_SET, settings)
            return _.get(res, 'data.data') // (!) return raw data
        } catch (e) {
            if (!axios.isCancel(e)) {
                commit(DOC_SETTINGS_SET, null)
                commit(`doc_viewer_metadata/${DOC_METADATA_SET}`, [], {root: true})
                Raven.captureException(e)
                showErrorMessage('Error fetching document settings')
            }
        }
    },
    [DOC_SETTINGS_REFRESH_VISIBLE_FIELDS_WITH_STATE_RULES_APPLIED]: async({commit, getters, rootGetters}) => {
        const doc = rootGetters['doc_viewer/docWithNormalizedParts']
        const fields = getters.visibleFields.filter(field => {
            const {isLine, isLandedCostLine} = getFieldPathMeta(field.path)
            if (isLine || isLandedCostLine) {
                return rootGetters['doc_viewer/lines'].some((line, idx) => {
                    return checkIsVisibleFieldInAccordanceWithRules({
                        field, idx, doc, fields: rootGetters['doc_viewer_settings/fields']
                    })
                })
            } else {
                return checkIsVisibleFieldInAccordanceWithRules({
                    field, doc, fields: rootGetters['doc_viewer_settings/fields']
                })
            }
        })
        if (isEqual(fields, getters.visibleFieldsWithStateRulesApplied)) {
            return
        }
        commit(DOC_SETTINGS_SET_VISIBLE_FIELDS_WITH_STATE_RULES_APPLIED, fields)
    }
}

const getValidatedFormSettings = ({settings = {}, type}) => {
    if (!_.isArray(settings.sections)) {
        settings.sections = []
    }
    if (!_.isArray(settings.fields)) {
        settings.fields = []
    }
    if (!_.isArray(settings.extensions)) {
        settings.extensions = []
    }
    if (!_.isArray(settings.quickActions)) {
        settings.quickActions = []
    }
    if (!_.isPlainObject(settings.toolbar)) {
        settings.toolbar = {}
    }

    if (!checkHasValidSubtypeFieldModel(settings.fields)) {
        settings.fields.unshift(getDocDefaultSubtypeFieldModel(type))
    }
    if (settings.sections.length === 0) {
        settings.sections.unshift(getDefaultSystemSectionByKey(FormSystemSections.HEADER))
    }
    settings.fields = getValidatedFormFieldsSettings(settings.fields)
    return settings
}

/*
 * NOTE: for API backwards compatibility
 */
const getValidatedFormFieldsSettings = (fields = []) => {
    return fields.map(f => {
        if (!_.isPlainObject(f.renderer)) {
            f.renderer = {}
        }
        return f
    })
}

/*
 * NOTE: (!) actual entities:
 *  'doc.section' field is type
 *  'doc.type' field is subtype
 */
const checkHasValidSubtypeFieldModel = (fields = []) => {
    const field = fields.find(f => _.get(f, 'path') === 'type')
    return field &&
        _.get(field, 'editor.type') === FieldEditorTypes.DD_SINGLE &&
        _.get(field, 'editor.select.source') === FieldEditorOptionsSources.API
}

const getDocDefaultSubtypeFieldModel = (type = '') => ({
    path: 'type',
    label: 'Type',
    section: {
        key: FormSystemSections.HEADER,
        order: -1,
        cols: 4
    },
    visible: true,
    editable: true,
    editor: {
        type: FieldEditorTypes.DD_SINGLE,
        select: {
            source: FieldEditorOptionsSources.API,
            trackBy: 'property',
            label: 'display',
            api: {
                url: `documents/types/${type}/subtypes`,
                method: 'GET'
            },
            filters: []
        }
    },
    renderer: {}
})

export default {
    state,
    getters,
    mutations,
    actions,
    namespaced: true
}
